import { useCallback, useState, useEffect, useMemo } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

// Constants for timing delays
const TOUR_DELAYS = {
    SCROLL_WAIT: 300, // Wait for scroll animation to complete
    START_DELAY: 500, // Initial delay before starting tour
} as const

interface TourStep {
    selector: string
    title: string
    description: string
    conditionBadge?: string
}

interface UseCreateProcessTourReturn {
    startTour: () => void
    endTour: () => void
    isActive: boolean
    currentTourStep: number
}

/**
 * Tour steps configuration for warehouse process creation
 * Extracted as constant for memoization and maintainability
 */
const TOUR_STEPS: TourStep[] = [
    {
        selector: '[data-tour="warehouse-process-form"]',
        title: 'Tạo Process mới',
        description:
            'Form tạo process giúp bạn quản lý các quy trình nhập/xuất/chuyển kho thiết bị.',
    },
    {
        selector: '[data-tour="warehouse-process-type-card"]',
        title: 'Loại Process *',
        description:
            'Chọn loại process phù hợp: Import (nhập kho), Export (xuất kho), Internal Transfer (chuyển kho nội bộ), hoặc Inventory Check (kiểm kê).',
    },
    {
        selector: '[data-tour="warehouse-process-type-selector"]',
        title: 'Chọn loại Process',
        description:
            'Click vào một trong các loại process. Import Purchase cho nhập mua mới, Import Return cho hàng trả về, Export Sale/Loan/Delivery cho xuất kho.',
    },
    {
        selector: '[data-tour="warehouse-process-details-card"]',
        title: 'Thông tin Process',
        description:
            'Nhập các thông tin chi tiết về process bao gồm ngày thực hiện, lý do và mô tả.',
    },
    {
        selector: '[data-tour="warehouse-process-date"]',
        title: 'Ngày Process *',
        description: 'Chọn ngày thực hiện process. Mặc định là ngày hôm nay.',
    },
    {
        selector: '[data-tour="warehouse-expected-date"]',
        title: 'Ngày dự kiến',
        description: 'Chọn ngày dự kiến hoàn thành process.',
        conditionBadge: 'Không bắt buộc',
    },
    {
        selector: '[data-tour="warehouse-reason"]',
        title: 'Lý do',
        description:
            'Nhập lý do thực hiện process. VD: "Nhập hàng mới", "Chuyển thiết bị phục vụ dự án ABC".',
        conditionBadge: 'Không bắt buộc',
    },
    {
        selector: '[data-tour="warehouse-description"]',
        title: 'Mô tả',
        description: 'Nhập mô tả chi tiết về process nếu cần.',
        conditionBadge: 'Không bắt buộc',
    },
    {
        selector: '[data-tour="warehouse-source-destination-card"]',
        title: 'Nguồn & Đích',
        description:
            'Chọn nguồn (Source) và đích (Destination) cho process. Các lựa chọn thay đổi tùy theo loại process.',
    },
    {
        selector: '[data-tour="warehouse-source-selector"]',
        title: 'Nguồn (Source) *',
        description:
            'Chọn nguồn của process. Có thể là Location (kho), Supplier (nhà cung cấp), hoặc Customer (khách hàng) tùy loại process.',
        conditionBadge: 'Thay đổi theo loại Process',
    },
    {
        selector: '[data-tour="warehouse-destination-selector"]',
        title: 'Đích (Destination) *',
        description:
            'Chọn đích của process. Có thể là Location (kho) hoặc Customer (khách hàng) tùy loại process.',
        conditionBadge: 'Thay đổi theo loại Process',
    },
    {
        selector: '[data-tour="warehouse-items-card"]',
        title: 'Danh sách Items',
        description:
            'Thêm các thiết bị/linh kiện vào process. Với Import Purchase/Return, bạn sẽ nhập thông tin item mới. Với các loại khác, chọn từ kho hiện có.',
    },
    {
        selector: '[data-tour="warehouse-add-items-btn"]',
        title: 'Thêm Items',
        description:
            'Click nút này để mở dialog chọn items từ kho. Bạn có thể tìm kiếm theo tên, service tag, hoặc serial number.',
        conditionBadge: 'Hiện với Export/Transfer/Check',
    },
    {
        selector: '[data-tour="warehouse-form-actions"]',
        title: 'Hoàn thành',
        description:
            'Sau khi điền đầy đủ thông tin, click "Create Process" để tạo. Process mới sẽ có trạng thái Draft và cần được phê duyệt.',
    },
]

/**
 * Tour hook for Warehouse Create Process form
 * Guides users through the process creation workflow
 *
 * Usage:
 * - Press Ctrl+Shift+H to start the tour
 * - Navigate using Enter/Arrow keys or click buttons
 * - Press Escape to close
 */
export const useCreateProcessTour = (): UseCreateProcessTourReturn => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)

    // Memoize tourSteps to prevent unnecessary re-renders
    const tourSteps = useMemo(() => TOUR_STEPS, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
    }, [])

    const showTourUI = useCallback(
        (step: TourStep, stepIndex: number) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Log warning for debugging in development
                if (process.env.NODE_ENV === 'development') {
                    console.warn(
                        `[Tour] Element not found: ${step.selector} - skipping step ${stepIndex + 1}`
                    )
                }
                // Skip to next step if element not found
                if (stepIndex < tourSteps.length - 1) {
                    // Recursive call with next index
                    const nextStep = tourSteps[stepIndex + 1]
                    if (nextStep) {
                        setCurrentTourStep(stepIndex + 1)
                        setTimeout(
                            () => showTourUI(nextStep, stepIndex + 1),
                            TOUR_DELAYS.SCROLL_WAIT
                        )
                    }
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            // Wait for scroll then add popover
            setTimeout(() => {
                const goToNextStep = () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        const nextStep = tourSteps[stepIndex + 1]
                        if (nextStep) {
                            cleanupTourElements()
                            setCurrentTourStep(stepIndex + 1)
                            showTourUI(nextStep, stepIndex + 1)
                        }
                    }
                }

                const goToPrevStep = () => {
                    const prevStep = tourSteps[stepIndex - 1]
                    if (prevStep) {
                        cleanupTourElements()
                        setCurrentTourStep(stepIndex - 1)
                        showTourUI(prevStep, stepIndex - 1)
                    }
                }

                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: tourSteps.length,
                    onPrev: stepIndex > 0 ? goToPrevStep : undefined,
                    onNext: goToNextStep,
                    onClose: endTour,
                    isLastStep: stepIndex === tourSteps.length - 1,
                    conditionBadge: step.conditionBadge,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, TOUR_DELAYS.SCROLL_WAIT)
        },
        [tourSteps, endTour]
    )

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)

        window.scrollTo({ top: 0, behavior: 'smooth' })

        setTimeout(() => {
            const firstStep = tourSteps[0]
            if (firstStep) {
                showTourUI(firstStep, 0)
            }
        }, TOUR_DELAYS.START_DELAY)
    }, [tourSteps, showTourUI])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive, currentTourStep }
}
